import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import { Typography, Unstable_Grid2 as Grid } from '@mui/material';
import { omit } from 'lodash';

import {
  ClientUrlParamsUpdateInput,
  useGetAllClientUrlParamsQuery,
  useUpdateClientUrlParamsMutation,
} from 'api';
import { useNotify } from 'hooks';
import { Queries } from 'components';
import { ClientUrlParamsSchema } from 'features/admin/validations';

import { CreateContentDomain, Parameter } from '../components/Parameters';
import { useQueryClient } from 'react-query';

const Parameters: FC = () => {
  const { t } = useTranslation();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const queryKey = useGetAllClientUrlParamsQuery.getKey();

  const { mutate } = useUpdateClientUrlParamsMutation({
    onError: notify.mutationError,
    onSuccess: () => {
      const message = t('components:savedSuccessfully');
      notify.info({ message });
    },
    onMutate: async params => {
      await queryClient.cancelQueries(queryKey);
      const queryData = queryClient.getQueryData(queryKey);
      (queryData as any).getAllClientUrlParams = [params.data];
      queryClient.setQueryData(queryKey, queryData);
    },
  });

  function handleSubmit(values: ClientUrlParamsUpdateInput) {
    const configData = omit(values, 'content_domain');
    const { configuration } = configData;

    const keySet = new Set(configuration.map(config => config.key));
    const hasUniqueKeys = configuration.length === keySet.size;
    const containsSpaces = configuration.some(config => {
      return config.key.indexOf(' ') >= 0;
    });

    if (!hasUniqueKeys) {
      return notify.error({ message: t('parameters:trackingKeyUnique') });
    }

    if (containsSpaces) {
      return notify.error({ message: t('parameters:trackingKeyOneWord') });
    }

    mutate({ data: configData });
  }

  return (
    <Queries.GetClientUrlParams>
      {urlParams => (
        <Fragment>
          <CreateContentDomain />
          <Typography variant="h5" color="textSecondary" sx={{ marginTop: 5, marginBottom: 3 }}>
            {t('admin:trackingRecords', { count: urlParams.length })}
          </Typography>
          <Grid container direction="column" spacing={3} wrap="nowrap">
            {urlParams.map(param => (
              <Grid key={param.id}>
                <Formik
                  initialValues={param}
                  onSubmit={handleSubmit}
                  validationSchema={ClientUrlParamsSchema}
                >
                  <Parameter param={param} />
                </Formik>
              </Grid>
            ))}
          </Grid>
        </Fragment>
      )}
    </Queries.GetClientUrlParams>
  );
};

export default Parameters;
